import { Icon, Icons } from 'components/Icon';
import React, { useEffect, useMemo, useState } from 'react';
import TextField from 'components/Form/TextField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'components/Form/Button';
import Spacer from 'components/Spacer';
import {
  useGetTrustee,
  useUpdateTrustee
} from 'common/queries-and-mutations/trustee';
import { BeneficiaryTypeEnum } from 'apps/user/types/user.enum.type';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import { UpdateTrusteesType } from 'apps/user/types/on-boarding.type';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { InfoModal } from 'components/Modal';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  trusteeId: number;
};

export default function EditTrusteesModal({
  onClose,
  trusteeId,
  onSuccess
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useUpdateTrustee();

  const { data, isFetched } = useGetTrustee({
    trusteeId
  });

  const trustee = useMemo(() => {
    if (isFetched && data) {
      return {
        trusteeId: data.id,
        trusteeFirstName: data.trusteeFirstName,
        trusteeMiddleName: data.trusteeMiddleName,
        trusteeLastName: data.trusteeLastName,
        trusteeType: data.trusteeType,
        address: data.address,
        emailAddress: data.emailAddress,
        phoneNumber: data.phoneNumber,
        dutiesInstruction: data.dutiesInstruction,
        rcNumber: data.rcNumber,
        incorporationDate: data.incorporationDate,
        incorporationPlace: data.incorporationPlace
      };
    } else {
      return {
        trusteeId: null,
        trusteeFirstName: '',
        trusteeMiddleName: '',
        trusteeLastName: '',
        trusteeType: '',
        address: '',
        emailAddress: '',
        phoneNumber: '',
        dutiesInstruction: '',
        rcNumber: '',
        incorporationDate: '',
        incorporationPlace: ''
      };
    }
  }, [data, isFetched]);

  const formik = useFormik({
    initialValues: { ...trustee },
    validationSchema: Yup.object({
      trusteeFirstName: Yup.string().when('trusteeType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string()
          .required('Trustee first name is required')
          .nullable(),
        otherwise: Yup.string().required('Name is required').nullable()
      }),
      trusteeLastName: Yup.string().when('trusteeType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string().required('Trustee last name is required').nullable(),
        otherwise: Yup.string()
      }),
      trusteeType: Yup.string().required('Trustee type is required').nullable(),
      address: Yup.string().required('Address is required').nullable(),
      emailAddress: Yup.string()
        .email('Invalid email')
        .required('Email is required')
        .nullable(),
      phoneNumber: Yup.string().when('trusteeType', {
        is: BeneficiaryTypeEnum.INDIVIDUAL,
        then: Yup.string().required('Phone number is required').nullable(),
        otherwise: Yup.string()
      }),
      dutiesInstruction: Yup.string().when('trusteeType', {
        is: BeneficiaryTypeEnum.CORPORATE,
        then: Yup.string()
          .required('Duties and Instructions is required')
          .nullable(),
        otherwise: Yup.string()
      }),
      rcNumber: Yup.string().when('trusteeType', {
        is: BeneficiaryTypeEnum.CORPORATE,
        then: Yup.string().required('RC Number is required').nullable(),
        otherwise: Yup.string()
      })
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onTrusteeSubmit(values);
    }
  });

  const onTrusteeSubmit = (formValue: UpdateTrusteesType) => {
    const payload = { trusteeId, updateTrusteesType: formValue };
    mutate(payload);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      onSuccess();
      toast(<Alert message="Trustee Successfully Updated" type="success" />);
      setIsSubmitting(false);
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <InfoModal
      width="w-[55%]"
      className="h-fit rounded-md px-4 pb-9 pt-5 sm:p-9"
    >
      <div>
        <div className="flex flex-row items-center justify-between pl-6">
          <p className="text-2xl font-bold text-[#353535]">Edit Trustee</p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="mt-10 px-6"
          onSubmit={formik.handleSubmit}
        >
          <div className="mb-1 mt-8">
            <div className="grid md:grid-cols-2 md:gap-10">
              <div className="group relative z-0 mb-6 w-full">
                <TextField
                  name={`trusteeFirstName`}
                  placeholder=""
                  value={formik.values.trusteeFirstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label={
                    trustee.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL
                      ? 'Trustee First Name'
                      : 'Name'
                  }
                  error={
                    formik.touched.trusteeFirstName
                      ? formik.errors.trusteeFirstName
                      : ''
                  }
                  isRequired
                  size="lg"
                  className="rounded-2xl border-[1.5px] border-gray-300"
                />
              </div>
              <div className="group relative z-0 mb-6 w-full">
                <div className="relative mb-2 flex items-center justify-between">
                  <span className="flex items-center">
                    <label className="text-[12px] font-medium text-[#353535]">
                      Type of Trustee
                    </label>
                    <div className="mx-2 mb-2">
                      <Spacer width={3} />
                      <Icon name={Icons.Required} />
                    </div>
                  </span>
                </div>
                <div className="mt-5 flex items-center gap-10">
                  <button
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        `trusteeType`,
                        BeneficiaryTypeEnum.INDIVIDUAL
                      )
                    }
                    className="flex items-center gap-3"
                  >
                    <span className="text-base font-bold text-[#353535]">
                      Individual
                    </span>
                    <Icon
                      name={
                        formik.values.trusteeType ===
                        BeneficiaryTypeEnum.INDIVIDUAL
                          ? Icons.CheckedBox
                          : Icons.UncheckBox
                      }
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      formik.setFieldValue(
                        `trusteeType`,
                        BeneficiaryTypeEnum.CORPORATE
                      )
                    }
                    className="flex items-center gap-3"
                  >
                    <span className="text-base font-bold text-[#353535]">
                      Corporate
                    </span>
                    <Icon
                      name={
                        formik.values.trusteeType ===
                        BeneficiaryTypeEnum.CORPORATE
                          ? Icons.CheckedBox
                          : Icons.UncheckBox
                      }
                    />
                  </button>
                </div>
              </div>
            </div>
            {formik.values.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL && (
              <div className="grid md:grid-cols-2 md:gap-10">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`trusteeMiddleName`}
                    placeholder=""
                    value={formik.values.trusteeMiddleName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Trustee Middle Name"
                    error={
                      formik.touched.trusteeMiddleName
                        ? formik.errors.trusteeMiddleName
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`trusteeLastName`}
                    placeholder=""
                    value={formik.values.trusteeLastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Trustee Last Name"
                    error={
                      formik.touched.trusteeLastName
                        ? formik.errors.trusteeLastName
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            )}
            {formik.values.trusteeType === BeneficiaryTypeEnum.INDIVIDUAL && (
              <div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`address`}
                      placeholder=""
                      isRequired
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Home Address"
                      error={
                        formik.touched.address ? formik.errors.address : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`emailAddress`}
                      placeholder=""
                      value={formik.values.emailAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Email Address"
                      isRequired
                      error={
                        formik.touched.emailAddress
                          ? formik.errors.emailAddress
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`phoneNumber`}
                      placeholder=""
                      isRequired
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Phone Number"
                      error={
                        formik.touched.phoneNumber
                          ? formik.errors.phoneNumber
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextAreaFieldWithAutoHeightResize
                      label="Duties and Instructions"
                      isRequired
                      name={`dutiesInstruction`}
                      value={formik.values.dutiesInstruction}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder=""
                      rows={6}
                      error={
                        formik.touched.dutiesInstruction
                          ? formik.errors.dutiesInstruction
                          : ''
                      }
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              </div>
            )}
            {formik.values.trusteeType === BeneficiaryTypeEnum.CORPORATE && (
              <div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`rcNumber`}
                      placeholder=""
                      value={formik.values.rcNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="RC Number"
                      isRequired
                      error={
                        formik.touched.rcNumber ? formik.errors.rcNumber : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`address`}
                      placeholder=""
                      isRequired
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Company Address"
                      error={
                        formik.touched.address ? formik.errors.address : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`emailAddress`}
                      placeholder=""
                      value={formik.values.emailAddress}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Email Address"
                      isRequired
                      error={
                        formik.touched.emailAddress
                          ? formik.errors.emailAddress
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`incorporationDate`}
                      placeholder=""
                      value={formik.values.incorporationDate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Date of Incorporation"
                      error={
                        formik.touched.incorporationDate
                          ? formik.errors.incorporationDate
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 md:gap-10">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`incorporationPlace`}
                      placeholder=""
                      value={formik.values.incorporationPlace}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Place of Incorporation"
                      error={
                        formik.touched.incorporationPlace
                          ? formik.errors.incorporationPlace
                          : ''
                      }
                      size="lg"
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextAreaFieldWithAutoHeightResize
                      label="Duties and Instructions"
                      isRequired
                      name={`dutiesInstruction`}
                      value={formik.values.dutiesInstruction}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder=""
                      rows={6}
                      error={
                        formik.touched.dutiesInstruction
                          ? formik.errors.dutiesInstruction
                          : ''
                      }
                      className="rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem]"
              label="Update Trustee"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
